import React, { useState, useEffect } from 'react';
import { Page } from './Page';
import { useTranslation } from 'react-i18next';

interface Section {
  id: string;
  title: string;
}

interface Props {
  icon: React.ReactNode;
  title: string;
  lastUpdated: string;
  contentsLabel: string;
  sections: Section[];
  contactEmail?: string;
  children: (activeSection: string) => React.ReactNode;
}

export const LegalPageLayout: React.FC<Props> = ({
  icon,
  title,
  lastUpdated,
  contentsLabel,
  sections,
  contactEmail = 'contact@betzen.app',
  children,
}) => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState(sections[0]?.id || '');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Page>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16 mt-12">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight flex items-center justify-center gap-3 sm:gap-4">
            <span className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-indigo-600/40 to-purple-600/40 backdrop-blur-sm border border-white/10 shadow-lg flex-shrink-0">
              <span className="text-lg sm:text-xl">{icon}</span>
            </span>
            {title}
          </h1>
          <div className="mt-3 flex items-center justify-center gap-2">
            <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
            <p className="text-sm text-gray-400">{lastUpdated}</p>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-28 rounded-xl bg-white/[0.03] backdrop-blur-md border border-white/[0.08] p-1.5 shadow-xl">
              <h2 className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
                {contentsLabel}
              </h2>
              <nav className="space-y-0.5">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                      activeSection === section.id
                        ? 'bg-gradient-to-r from-indigo-600/30 to-purple-600/30 text-white shadow-sm border border-white/10'
                        : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span
                        className={`w-1.5 h-1.5 rounded-full transition-colors ${
                          activeSection === section.id
                            ? 'bg-teal-400'
                            : 'bg-gray-600'
                        }`}
                      />
                      {section.title}
                    </span>
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <main className="lg:col-span-3">
            <div className="rounded-xl bg-white/[0.03] backdrop-blur-md border border-white/[0.08] p-6 sm:p-8 shadow-xl min-h-[400px]">
              {children(activeSection)}
            </div>

            {/* Contact footer */}
            <div className="mt-6 rounded-xl bg-gradient-to-r from-indigo-900/40 to-purple-900/40 backdrop-blur-sm border border-white/[0.08] p-6 text-center">
              <p className="text-sm text-gray-400 mb-3">
                {t('legalPage.contactPrompt')}
              </p>
              {contactEmail && (
                <a
                  href={`mailto:${contactEmail}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-medium hover:from-indigo-500 hover:to-purple-500 transition-all shadow-lg shadow-indigo-600/20"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  {contactEmail}
                </a>
              )}
            </div>
          </main>
        </div>
      </div>
    </Page>
  );
};
